import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase, uniqueChannelTopic } from '@/lib/supabase';
import { authEvents } from '@/lib/auth-events';
import { useAuth } from '@/contexts/AuthContext';

// Narrow `CHANNEL_ERROR` signals to auth-related failures only. Network blips
// and reconnect stalls surface as CHANNEL_ERROR too, and we must not
// force-logout for those. The bare word "token" is too broad — rate-limiter
// diagnostics and websocket-handshake errors mention "token" without being
// auth failures — so we require a modifier (jwt / unauthorized / invalid_token
// / expired token / invalid token).
const AUTH_REALTIME_RE = /jwt|unauthorized|invalid[ _]token|token[ _](expired|invalid|revoked)/i;

function handleChannelStatus(status: string, err?: Error) {
  // A channel that fails to join is otherwise invisible: no events arrive
  // and nothing in the UI hints at it. Keep the failure in the console.
  if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
    console.warn(`[realtime] channel ${status}`, err?.message ?? '');
  }
  if (status !== 'CHANNEL_ERROR' || !err) return;
  if (AUTH_REALTIME_RE.test(err.message ?? String(err))) {
    authEvents.emitInvalidSession('session_invalid');
  }
}

/**
 * Subscribes to Supabase Realtime changes for a specific group.
 * When data changes, invalidates the relevant TanStack Query caches
 * so the UI auto-refetches.
 */
export function useGroupRealtimeSubscription(groupId: string | undefined) {
  const queryClient = useQueryClient();
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  useEffect(() => {
    if (!groupId) return;

    const channel = supabase
      .channel(uniqueChannelTopic(`group-${groupId}`))
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'expenses', filter: `group_id=eq.${groupId}` },
        (payload) => {
          queryClient.invalidateQueries({ queryKey: ['expenses', groupId] });
          // The global Expenses list is not mounted while a group/expense
          // detail is open, and with the 2-minute staleTime it would show a
          // just-deleted expense on return unless it is invalidated here too.
          queryClient.invalidateQueries({ queryKey: ['all-expenses'] });
          queryClient.invalidateQueries({ queryKey: ['balances', groupId] });
          queryClient.invalidateQueries({ queryKey: ['groupBudget', groupId] });
          queryClient.invalidateQueries({ queryKey: ['dashboard-budgets'] });
          queryClient.invalidateQueries({ queryKey: ['dashboard-balances'] });
          // Also invalidate individual expense detail cache
          const expenseId = (payload.new as any)?.id ?? (payload.old as any)?.id;
          if (expenseId) {
            queryClient.invalidateQueries({ queryKey: ['expense', expenseId] });
          }
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'expense_splits', filter: `group_id=eq.${groupId}` },
        (payload) => {
          queryClient.invalidateQueries({ queryKey: ['expenses', groupId] });
          queryClient.invalidateQueries({ queryKey: ['all-expenses'] });
          queryClient.invalidateQueries({ queryKey: ['balances', groupId] });
          queryClient.invalidateQueries({ queryKey: ['groupBudget', groupId] });
          queryClient.invalidateQueries({ queryKey: ['dashboard-budgets'] });
          queryClient.invalidateQueries({ queryKey: ['dashboard-balances'] });
          const expenseId = (payload.new as any)?.expense_id ?? (payload.old as any)?.expense_id;
          if (expenseId) {
            queryClient.invalidateQueries({ queryKey: ['expense', expenseId] });
          }
        }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'groups', filter: `id=eq.${groupId}` },
        () => {
          // Group row changed (e.g. budget config updated from mobile). Refresh
          // the group detail + dashboard entries so budget fields stay in sync.
          queryClient.invalidateQueries({ queryKey: ['group', groupId] });
          queryClient.invalidateQueries({ queryKey: ['groups'] });
          queryClient.invalidateQueries({ queryKey: ['groupBudget', groupId] });
          queryClient.invalidateQueries({ queryKey: ['dashboard-budgets'] });
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'settlements', filter: `group_id=eq.${groupId}` },
        () => {
          queryClient.invalidateQueries({ queryKey: ['settlements', groupId] });
          queryClient.invalidateQueries({ queryKey: ['balances', groupId] });
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'group_members', filter: `group_id=eq.${groupId}` },
        () => {
          queryClient.invalidateQueries({ queryKey: ['group', groupId] });
          queryClient.invalidateQueries({ queryKey: ['groups'] });
          queryClient.invalidateQueries({ queryKey: ['member-lookup', groupId] });
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'expense_comments', filter: `group_id=eq.${groupId}` },
        (payload) => {
          const expenseId = (payload.new as any)?.expense_id ?? (payload.old as any)?.expense_id;
          if (expenseId) {
            queryClient.invalidateQueries({ queryKey: ['comments', expenseId] });
          }
          queryClient.invalidateQueries({ queryKey: ['comment-counts', groupId] });
          queryClient.invalidateQueries({ queryKey: ['activities'] });
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'expense_disputes', filter: `group_id=eq.${groupId}` },
        (payload) => {
          const expenseId = (payload.new as any)?.expense_id ?? (payload.old as any)?.expense_id;
          if (expenseId) {
            queryClient.invalidateQueries({ queryKey: ['disputes', expenseId] });
          }
          queryClient.invalidateQueries({ queryKey: ['dispute-counts', groupId] });
          queryClient.invalidateQueries({ queryKey: ['expenses', groupId] });
          queryClient.invalidateQueries({ queryKey: ['activities'] });
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'categories', filter: `group_id=eq.${groupId}` },
        () => {
          // Custom group categories added/renamed on another device.
          queryClient.invalidateQueries({ queryKey: ['categories'] });
        }
      )
      .subscribe(handleChannelStatus);

    channelRef.current = channel;

    return () => {
      supabase.removeChannel(channel);
      channelRef.current = null;
    };
  }, [groupId, queryClient]);
}

/**
 * Subscribes to cross-group expense + group changes so the dashboard and
 * global expense list (balances, budgets, group cards, all-expenses)
 * reflect mutations made on another device. Relies on RLS to filter
 * events down to rows the current user can see — no explicit group_id
 * filter needed.
 */
export function useDashboardRealtimeSubscription() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;

    const invalidateExpenseCaches = () => {
      // Prefix-match invalidation: ['expenses'] matches ['expenses', groupId],
      // ['all-expenses'] matches ['all-expenses', filters].
      queryClient.invalidateQueries({ queryKey: ['all-expenses'] });
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      queryClient.invalidateQueries({ queryKey: ['balances'] });
      queryClient.invalidateQueries({ queryKey: ['groupBudget'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-balances'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-budgets'] });
    };

    const channel = supabase
      .channel(uniqueChannelTopic(`dashboard-${user.id}`))
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'expenses' },
        invalidateExpenseCaches
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'expense_splits' },
        invalidateExpenseCaches
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'groups' },
        () => {
          // Budget config changed on another device.
          queryClient.invalidateQueries({ queryKey: ['groups'] });
          queryClient.invalidateQueries({ queryKey: ['dashboard-budgets'] });
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'settlements' },
        () => {
          // Settle-up recorded on another device changes every balance view.
          queryClient.invalidateQueries({ queryKey: ['settlements'] });
          queryClient.invalidateQueries({ queryKey: ['balances'] });
          queryClient.invalidateQueries({ queryKey: ['dashboard-balances'] });
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'group_members' },
        () => {
          // Another member joined/left/was renamed in one of our groups.
          queryClient.invalidateQueries({ queryKey: ['groups'] });
          queryClient.invalidateQueries({ queryKey: ['group'] });
          queryClient.invalidateQueries({ queryKey: ['member-lookup'] });
          queryClient.invalidateQueries({ queryKey: ['dashboard-balances'] });
        }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'users' },
        () => {
          // Co-member profile (display name / avatar) edited on another device.
          queryClient.invalidateQueries({ queryKey: ['member-lookup'] });
          queryClient.invalidateQueries({ queryKey: ['group'] });
          queryClient.invalidateQueries({ queryKey: ['groups'] });
        }
      )
      .subscribe(handleChannelStatus);

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, queryClient]);
}

/**
 * Subscribes to notification changes for the current user.
 */
export function useNotificationRealtimeSubscription() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel(uniqueChannelTopic(`notifications-${user.id}`))
      .on(
        'postgres_changes',
        // '*' rather than INSERT: read/dismissed state changed on mobile must
        // clear the badge here too.
        { event: '*', schema: 'public', table: 'notifications', filter: `user_id=eq.${user.id}` },
        () => {
          queryClient.invalidateQueries({ queryKey: ['notifications'] });
        }
      )
      .subscribe(handleChannelStatus);

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, queryClient]);
}
